import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccountingDraft, useDebounce } from '@saiens/shared/hooks';
import { formatDate } from '@saiens/shared/utils';
import type { AccountingAcceptanceItem } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ArrowLeft, ChevronRight } from 'lucide-react';

export function DraftListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [myOrders, setMyOrders] = useState(false);

  const { data, isLoading, error } = useAccountingDraft({
    search: debouncedSearch,
    myOrders: myOrders || undefined,
    limit: 50,
  });

  const items = data?.items ?? [];

  return (
    <div className="px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate('/accounting/overview')} className="text-gray-500">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">草稿/待驗收</h1>
          <p className="text-sm text-gray-500">尚未簽核的驗收單</p>
        </div>
      </div>

      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋驗收編號、案名、客戶..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <div className="flex gap-2">
          <FilterChip label="我的案件" active={myOrders} onClick={() => setMyOrders((p) => !p)} />
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有資料</div>
        ) : (
          <div className="divide-y">
            {items.map((item) => (
              <DraftCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {data && items.length > 0 && (
        <p className="mt-4 text-center text-sm text-gray-500">
          {data.total > items.length
            ? `顯示 ${items.length} / ${data.total} 筆`
            : `共 ${data.total} 筆`}
        </p>
      )}
    </div>
  );
}

function DraftCard({ item }: { item: AccountingAcceptanceItem }) {
  return (
    <Link to={`/acceptance/${item.id}`} className="flex items-center justify-between p-4 hover:bg-gray-50">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900">
          {item.project_name || item.sale_order_name || item.name}
        </p>
        <p className="truncate text-sm text-gray-500">{item.partner_name || '---'}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
          <span>{item.name}</span>
          {item.create_date && <span>建立: {formatDate(item.create_date)}</span>}
          {item.employee_name && <span>{item.employee_name}</span>}
        </div>
      </div>
      <div className="ml-4 flex flex-col items-end gap-1">
        <span className="text-sm font-medium text-gray-700">
          NT${item.amount_total.toLocaleString()}
        </span>
        <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          草稿
        </span>
        <ChevronRight className="h-4 w-4 text-gray-300" />
      </div>
    </Link>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
